import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input, OnDestroy,
    Output,
    ViewChild
} from '@angular/core';
import paper from 'paper';
import {isDefined} from '../../utils/utils';

/**
 * Generic component to work with canvases (based on paperJs)
 */
@Component({
    selector: 'wm-canvas',
    templateUrl: './canvas.component.html',
    styleUrls: ['./canvas.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CanvasComponent implements AfterViewInit, OnDestroy {

    @ViewChild('canvas') theCanvas: ElementRef;

    private _paperScope: paper.PaperScope;

    private layers: paper.Layer[] = [];
    private canvasParentWidth?: number = undefined;

    @Input()
    trackMouse? = false

    @Output()
    mouseMoveEvent = new EventEmitter<{x: any, y: any}>();

    constructor() {
        this._paperScope = new paper.PaperScope();
    }

    activateAndGetPaperScope(): any {
        if (!!this._paperScope) {
            this._paperScope.activate();
            return this._paperScope;
        }
    }

    ngOnDestroy() {
        if (this._paperScope && this._paperScope.project) {
            this._paperScope.tool?.remove();
            this._paperScope.project?.clear();
            this._paperScope.view?.remove();
            this._paperScope.project?.remove();
            this._paperScope = null as any;
            // aggressively releasing canvas memory
            this.theCanvas.nativeElement.width = 0;
            this.theCanvas.nativeElement.height = 0;
        }
    }

    getPaperScope(): any {
        return this._paperScope;
    }

    scaleCanvas() {
        const canvasElem = this.theCanvas.nativeElement as HTMLCanvasElement;
        this.canvasParentWidth = canvasElem.parentElement.offsetWidth;
        canvasElem.style.width = `${this.canvasParentWidth}px`;
    }

    resetScaling() {
        // TODO check if this is missing an implementation because it is used
    }

    /**
     * Add new layer without activation.
     */
    addLayer(name: string) {
        let scope = this.activateAndGetPaperScope();
        let layer = new scope.Layer();
        layer.name = name;
        this.layers = [...this.layers, layer];
    }

    // /**
    //  * Remove layer with given name.
    //  */
    // removeLayer(name: string) {
    //   let scope = this.activateAndGetPaperScope();
    //   let layer = this.layers.find(item => item.name === name);
    //
    //   if (! isDefined(layer)) {
    //     return;
    //   }
    //
    //   this.layers = this.layers.filterNot(item => item.name === name);
    //   layer.removeChildren();
    //   layer.remove();
    // }

    /**
     * Convenience helper for adding layer, activating Paperscope and layer, and returning layer.
     */
    addAndActivateLayer(name: string): paper.Layer | null {
        this.addLayer(name);
        return this.activateAndGetLayer(name);
    }

    removeLayer(name: string) {
        if (!this._paperScope?.project) {
            let layers = Array.from(this._paperScope.project.layers);
            layers.forEach((layer: any) => {
                if (layer.name === name) {
                    layer.remove();
                    this.layers = this.layers.filter(item => item.name !== name);
                }
            });
        }
    }

    removeAllLayers() {
        if (!this._paperScope?.project) return;
        let layers = Array.from(this._paperScope.project.layers);
        layers.forEach((layer: any) => {
            layer.remove();
        });
        this.layers = [];
    }

    /**
     * Return an activated layer or null if no layer with that name is known.
     */
    activateAndGetLayer(name: string): paper.Layer | null {
        if (!this._paperScope) {
            return;
        }

        let layer = this.layers.find(item => item.name === name);

        if (!isDefined(layer)) {
            return null;
        }

        this._paperScope.activate();
        layer.activate();
        return layer;
    }

    ngAfterViewInit() {
        this.resize();
    }

    resize() {
        this.scaleCanvas();
        try {
            // delete old paper scope
            if (this._paperScope && this._paperScope.project) {
                this._paperScope.project.remove();
            }
            if (!this._paperScope?.project) {
                this._paperScope.setup(this.theCanvas.nativeElement);
            }
        } catch (e) {
            console.error(e)
        }
    }
}
